// ============================================================================
// CLEO'S SECRET — MyResults
// File: src/pages/MyResults.jsx
//
// Patient view of pathology results. Inherits the dashboard pattern from
// ClientPortal: header strip + SectionHead sections + cards.
//
// Each approved result is a collapsible card with rich expandable content:
//   • GP note, greeting, overall picture
//   • Good news / Areas to explore (themed boxes with diamond bullets)
//   • Markers grouped by category, each with flag-colored left bar
//   • Questions for GP
//   • Lifestyle context, reassurance, disclaimer
//   • Save-as-PDF + Book-consultation actions
//
// Pending results render as separate "being prepared" cards above the
// approved list. Empty state when neither pending nor approved.
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  FF, FS, P, T,
  Eyebrow,
  BtnPrimary, BtnGhost,
  Spinner,
} from '../lib/design-tokens';
import Ankh from '../components/Ankh';

// ───────── Date formatters ─────────
function formatDate(s) {
  if (!s) return '';
  return new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
}
function formatDateShort(s) {
  if (!s) return '';
  return new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ───────── Flag → color, label, bg ─────────
function flagColor(flag) {
  if (flag === 'optimal') return P.GREEN;
  if (flag === 'normal') return P.TF;
  if (flag === 'borderline') return P.AMBER;
  if (flag === 'high' || flag === 'low') return P.RED;
  return P.TF;
}
function flagBg(flag) {
  if (flag === 'optimal') return P.GREEN_BG;
  if (flag === 'normal') return 'rgba(122,96,64,0.06)';
  if (flag === 'borderline') return P.AMBER_BG;
  if (flag === 'high' || flag === 'low') return P.RED_BG;
  return 'transparent';
}
function flagLabel(flag) {
  if (flag === 'optimal') return 'Optimal';
  if (flag === 'normal') return 'Within range';
  if (flag === 'borderline') return 'Borderline';
  if (flag === 'high') return 'Above range';
  if (flag === 'low') return 'Below range';
  return flag;
}

// ───────── Marker category labels ─────────
function categoryLabel(cat) {
  var labels = {
    thyroid: 'Thyroid',
    sex_hormones: 'Sex hormones',
    metabolic: 'Metabolic',
    iron_studies: 'Iron studies',
    vitamins_minerals: 'Vitamins and minerals',
    inflammation: 'Inflammation',
    liver_kidney: 'Liver and kidney',
    full_blood_count: 'Full blood count',
    other: 'Other',
  };
  return labels[cat] || cat;
}

// ───────── SectionHead (same as ClientPortal) ─────────
function SectionHead({ label, count }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'baseline',
      gap: 12,
      marginBottom: 'clamp(18px, 2vw, 24px)',
      paddingBottom: 'clamp(12px, 1.4vw, 16px)',
      borderBottom: '1px solid rgba(154,116,48,0.22)',
    }}>
      <h2 style={{ ...T.label, color: P.BR2, margin: 0 }}>{label}</h2>
      {(count !== undefined && count !== null) && (
        <span style={{
          fontFamily: FF,
          fontSize: 'clamp(12px, 1vw, 14px)',
          fontWeight: 700,
          letterSpacing: '0.34em',
          color: P.TL,
        }}>{String(count).padStart(2, '0')}</span>
      )}
    </div>
  );
}

// ───────── FlagBadge — themed pill for marker flag ─────────
function FlagBadge({ flag }) {
  return (
    <span style={{
      fontFamily: FF,
      fontSize: 'clamp(11px, 0.95vw, 13px)',
      fontWeight: 700,
      letterSpacing: '0.34em',
      textTransform: 'uppercase',
      color: flagColor(flag),
      background: flagBg(flag),
      padding: '6px 12px',
      border: '1px solid ' + flagColor(flag),
      whiteSpace: 'nowrap',
      flexShrink: 0,
    }}>{flagLabel(flag)}</span>
  );
}

// ───────── ThemedBox — colored callout (good news / areas / etc.) ─────────
function ThemedBox({ label, items, color, bg, children }) {
  return (
    <div style={{
      padding: 'clamp(20px, 2.4vw, 32px)',
      background: bg,
      border: '1px solid ' + color,
      borderLeft: '3px solid ' + color,
      marginBottom: 'clamp(18px, 2vw, 24px)',
    }}>
      <div style={{
        ...T.label,
        fontSize: 'clamp(11px, 0.95vw, 13px)',
        color: color,
        marginBottom: 'clamp(12px, 1.4vw, 18px)',
      }}>{label}</div>
      {items ? (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {items.map(function(item, i) {
            return (
              <li key={i} style={{
                position: 'relative',
                paddingLeft: 28,
                marginBottom: i === items.length - 1 ? 0 : 14,
                ...T.bodyMd,
                color: P.TX,
                lineHeight: 1.75,
              }}>
                <span style={{
                  position: 'absolute',
                  left: 8,
                  top: '0.6em',
                  width: 8,
                  height: 8,
                  background: color,
                  transform: 'rotate(45deg)',
                }} />
                {item}
              </li>
            );
          })}
        </ul>
      ) : children}
    </div>
  );
}

// ───────── MarkerCard ─────────
function MarkerCard({ marker }) {
  return (
    <div style={{
      padding: 'clamp(16px, 1.8vw, 22px)',
      background: P.PAP,
      border: '1px solid ' + P.SAND3,
      borderLeft: '3px solid ' + flagColor(marker.flag),
      marginBottom: 10,
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 14,
        marginBottom: 8,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            ...T.label,
            fontSize: 'clamp(11px, 0.95vw, 13px)',
            color: P.BR,
            marginBottom: 6,
          }}>{marker.marker_name}</div>
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(22px, 2.2vw, 30px)',
            fontWeight: 500,
            color: P.INK,
            lineHeight: 1.1,
            letterSpacing: '-0.012em',
          }}>
            {marker.value}{' '}
            <span style={{
              fontSize: '0.6em',
              fontWeight: 400,
              color: P.TF,
              letterSpacing: 0,
            }}>{marker.unit}</span>
          </div>
          <div style={{
            ...T.bodySm,
            color: P.TF,
            marginTop: 6,
            fontStyle: 'italic',
            fontWeight: 500,
          }}>Reference: {marker.reference_range}</div>
        </div>
        <FlagBadge flag={marker.flag} />
      </div>

      {marker.ai_interpretation && (
        <div style={{
          ...T.bodyMd,
          color: P.TL,
          fontStyle: 'italic',
          marginTop: 14,
          paddingTop: 14,
          borderTop: '1px dashed ' + P.SAND3,
          lineHeight: 1.75,
        }}>{marker.ai_interpretation}</div>
      )}

      {marker.optimal_range_note && (
        <div style={{
          ...T.bodySm,
          color: P.TF,
          marginTop: 8,
        }}>{marker.optimal_range_note}</div>
      )}
    </div>
  );
}

// ───────── ResultCard — collapsible pathology result ─────────
function ResultCard({ result, expanded, onToggle, onNavigate }) {
  var ai = result.ai_content || {};
  var ps = ai.patient_summary || {};
  var upload = result.pathology_uploads || {};

  // Group markers by category
  var markersByCategory = {};
  (ai.markers || []).forEach(function(m) {
    var cat = m.marker_category || 'other';
    if (!markersByCategory[cat]) markersByCategory[cat] = [];
    markersByCategory[cat].push(m);
  });
  var categoryKeys = Object.keys(markersByCategory);

  return (
    <div style={{
      background: P.PAP,
      border: '1px solid ' + P.SAND3,
      marginBottom: 14,
    }}>
      {/* Header (clickable) */}
      <div
        onClick={onToggle}
        style={{
          padding: 'clamp(20px, 2.2vw, 28px)',
          cursor: 'pointer',
          borderBottom: expanded ? '1px solid ' + P.SAND3 : 'none',
        }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              ...T.label,
              fontSize: 'clamp(11px, 0.9vw, 13px)',
              color: P.BR,
              marginBottom: 8,
            }}>Pathology results</div>
            <div style={{
              fontFamily: FF,
              fontSize: 'clamp(18px, 1.6vw, 22px)',
              fontWeight: 500,
              color: P.INK,
              lineHeight: 1.25,
              marginBottom: 6,
              letterSpacing: '-0.005em',
            }}>
              {upload.collection_date
                ? 'Collected ' + formatDate(upload.collection_date)
                : 'Results from ' + formatDateShort(upload.uploaded_at)}
            </div>
            <div style={{
              ...T.bodySm,
              color: P.TF,
              letterSpacing: '0.04em',
            }}>
              {upload.laboratory ? upload.laboratory + ' \u00b7 ' : ''}
              {(ai.markers || []).length} markers \u00b7 Released {formatDateShort(result.released_to_patient_at)}
            </div>
          </div>
          <span style={{
            fontFamily: FF,
            fontSize: 28,
            color: P.GO,
            lineHeight: 1,
            flexShrink: 0,
          }} aria-hidden="true">{expanded ? '−' : '+'}</span>
        </div>
      </div>

      {/* Body */}
      {expanded && (
        <div style={{ padding: 'clamp(20px, 2.4vw, 32px)' }}>

          {/* GP notes */}
          {result.gp_notes && (
            <ThemedBox label="Note from your GP" color={P.GO} bg="rgba(201,169,97,0.06)">
              <p style={{
                ...T.bodyMd,
                color: P.TX,
                lineHeight: 1.75,
              }}>{result.gp_notes}</p>
            </ThemedBox>
          )}

          {/* Greeting */}
          {ps.greeting && (
            <div style={{
              ...T.premise,
              fontSize: 'clamp(18px, 1.8vw, 22px)',
              color: P.TX,
              fontStyle: 'italic',
              marginBottom: 'clamp(20px, 2.2vw, 28px)',
              paddingLeft: 'clamp(16px, 1.6vw, 22px)',
              borderLeft: '2px solid ' + P.GO,
              lineHeight: 1.55,
            }}>{ps.greeting}</div>
          )}

          {/* Overall picture */}
          {ps.overall_picture && (
            <p style={{
              ...T.bodyLg,
              color: P.TX,
              textAlign: 'justify',
              hyphens: 'auto',
              marginBottom: 'clamp(24px, 2.6vw, 32px)',
            }}>{ps.overall_picture}</p>
          )}

          {/* Good news */}
          {ps.good_news && ps.good_news.length > 0 && (
            <ThemedBox
              label="Good news"
              items={ps.good_news}
              color={P.GREEN}
              bg={P.GREEN_BG}
            />
          )}

          {/* Areas to explore */}
          {ps.areas_to_explore && ps.areas_to_explore.length > 0 && (
            <ThemedBox
              label="Areas to explore with your GP"
              items={ps.areas_to_explore}
              color={P.AMBER}
              bg={P.AMBER_BG}
            />
          )}

          {/* Markers */}
          {categoryKeys.length > 0 && (
            <div style={{ marginTop: 'clamp(28px, 3vw, 40px)' }}>
              <SectionHead label="Your markers" count={(ai.markers || []).length} />

              {categoryKeys.map(function(cat) {
                var markers = markersByCategory[cat];
                return (
                  <div key={cat} style={{ marginBottom: 'clamp(24px, 2.6vw, 36px)' }}>
                    <div style={{
                      fontFamily: FF,
                      fontSize: 'clamp(12px, 1vw, 14px)',
                      fontWeight: 700,
                      letterSpacing: '0.34em',
                      textTransform: 'uppercase',
                      color: P.BR2,
                      marginBottom: 12,
                      paddingBottom: 8,
                      borderBottom: '1px solid ' + P.SAND3,
                    }}>{categoryLabel(cat)}</div>
                    {markers.map(function(m, i) {
                      return <MarkerCard key={i} marker={m} />;
                    })}
                  </div>
                );
              })}
            </div>
          )}

          {/* Questions for GP */}
          {ps.questions_for_gp && ps.questions_for_gp.length > 0 && (
            <div style={{ marginTop: 'clamp(28px, 3vw, 40px)' }}>
              <SectionHead label="Questions for your consultation" count={ps.questions_for_gp.length} />
              {ps.questions_for_gp.map(function(q, i) {
                return (
                  <div key={i} style={{
                    padding: 'clamp(16px, 1.8vw, 22px)',
                    background: 'rgba(201,169,97,0.05)',
                    border: '1px solid ' + P.SAND3,
                    marginBottom: 10,
                    display: 'flex',
                    gap: 16,
                    alignItems: 'flex-start',
                  }}>
                    <span style={{
                      fontFamily: FF,
                      fontSize: 'clamp(22px, 2vw, 28px)',
                      fontWeight: 400,
                      fontStyle: 'italic',
                      color: P.GO,
                      lineHeight: 1,
                      flexShrink: 0,
                      marginTop: 2,
                    }}>?</span>
                    <p style={{
                      ...T.bodyMd,
                      color: P.TX,
                      lineHeight: 1.75,
                    }}>{q}</p>
                  </div>
                );
              })}
            </div>
          )}

          {/* Lifestyle context */}
          {ps.lifestyle_context && (
            <ThemedBox label="Lifestyle context" color={P.SAND3} bg={P.PAP}>
              <p style={{
                ...T.bodyMd,
                color: P.TX,
                lineHeight: 1.75,
              }}>{ps.lifestyle_context}</p>
            </ThemedBox>
          )}

          {/* Reassurance */}
          {ps.reassurance && (
            <div style={{
              padding: 'clamp(24px, 2.8vw, 36px)',
              background: 'rgba(201,169,97,0.06)',
              border: '1px solid ' + P.GO,
              borderLeft: '3px solid ' + P.GO,
              textAlign: 'center',
              ...T.premise,
              fontSize: 'clamp(17px, 1.6vw, 22px)',
              color: P.TX,
              marginBottom: 'clamp(24px, 2.6vw, 32px)',
              lineHeight: 1.6,
            }}>{ps.reassurance}</div>
          )}

          {/* Disclaimer (em dash removed) */}
          <div style={{
            ...T.bodySm,
            fontSize: 'clamp(12px, 0.95vw, 13px)',
            color: P.TF,
            fontStyle: 'italic',
            textAlign: 'center',
            lineHeight: 1.75,
            marginTop: 'clamp(20px, 2.2vw, 28px)',
            padding: 'clamp(14px, 1.6vw, 20px)',
          }}>
            This interpretation has been reviewed and approved by your GP. It is for educational purposes and to support your conversation with your clinician, not a diagnosis. If you have new or worsening symptoms, please contact your GP.
          </div>

          {/* Actions */}
          <div style={{
            display: 'flex',
            gap: 12,
            marginTop: 'clamp(20px, 2.2vw, 28px)',
            flexWrap: 'wrap',
          }} className="cs-result-actions">
            <div style={{ flex: 1, minWidth: 200 }}>
              <button
                onClick={function() { window.print(); }}
                style={{
                  width: '100%',
                  fontFamily: FS,
                  fontSize: 'clamp(11px, 0.95vw, 12px)',
                  fontWeight: 600,
                  letterSpacing: '0.28em',
                  textTransform: 'uppercase',
                  padding: '14px 28px',
                  background: 'transparent',
                  color: P.BR,
                  border: '1px solid ' + P.GO,
                  cursor: 'pointer',
                  transition: 'background 0.2s, color 0.2s',
                }}
                onMouseEnter={function(e) {
                  e.currentTarget.style.background = 'rgba(201,169,97,0.08)';
                }}
                onMouseLeave={function(e) {
                  e.currentTarget.style.background = 'transparent';
                }}>
                Save as PDF
              </button>
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <button
                onClick={function() { if (onNavigate) onNavigate('scheduler'); }}
                style={{
                  width: '100%',
                  fontFamily: FS,
                  fontSize: 'clamp(11px, 0.95vw, 12px)',
                  fontWeight: 600,
                  letterSpacing: '0.28em',
                  textTransform: 'uppercase',
                  padding: '14px 28px',
                  background: P.INK,
                  color: P.PAP,
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background 0.2s',
                }}
                onMouseEnter={function(e) {
                  e.currentTarget.style.background = P.BR;
                }}
                onMouseLeave={function(e) {
                  e.currentTarget.style.background = P.INK;
                }}>
                Book consultation
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// MyResults
// ════════════════════════════════════════════════════════════════════════════
export default function MyResults(props) {
  var onNavigate = props.onNavigate;
  var email = props.email;

  var dataState = useState(null);
  var data = dataState[0];
  var setData = dataState[1];

  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var expandedState = useState(null);
  var expanded = expandedState[0];
  var setExpanded = expandedState[1];

  useEffect(function() {
    if (!email) { setLoading(false); return; }
    fetch('/api/get-patient-results', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email })
    })
      .then(function(res) { return res.json(); })
      .then(function(result) { setData(result); setLoading(false); })
      .catch(function() { setLoading(false); });
  }, [email]);

  if (loading) {
    return (
      <div style={{
        fontFamily: FF,
        background: P.SAND,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Spinner color={P.GO} size={48} />
      </div>
    );
  }

  var results = (data && data.results) || [];
  var pending = (data && data.pending) || [];

  return (
    <div style={{ fontFamily: FF, background: P.SAND, minHeight: '100vh' }}>

      {/* ═════════ Header strip ═════════ */}
      <header style={{
        background: P.SAND,
        padding: 'clamp(36px, 4.5vw, 64px) clamp(20px, 3vw, 40px) clamp(28px, 3.5vw, 48px)',
        textAlign: 'center',
        borderBottom: '1px solid ' + P.SAND3,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}>
        <div style={{ marginBottom: 'clamp(20px, 2.2vw, 28px)', display: 'flex', justifyContent: 'center' }}>
          <Ankh responsive="opener" color={P.GO} />
        </div>
        <div style={{ marginBottom: 16 }}>
          <Eyebrow center>Pathology</Eyebrow>
        </div>
        <h1 style={{
          fontFamily: FF,
          fontSize: 'clamp(28px, 3.4vw, 44px)',
          fontWeight: 400,
          lineHeight: 1.12,
          letterSpacing: '-0.012em',
          color: P.INK,
          margin: 0,
        }}>
          Your <span style={{ fontStyle: 'italic', color: P.BR2 }}>results.</span>
        </h1>
        <div style={{
          ...T.bodySm,
          color: P.TF,
          marginTop: 10,
          letterSpacing: '0.04em',
        }}>
          Personalised interpretation &middot; GP approved
        </div>
      </header>

      {/* ═════════ Main content ═════════ */}
      <main style={{
        maxWidth: 920,
        margin: '0 auto',
        padding: 'clamp(32px, 4vw, 56px) clamp(20px, 3vw, 40px) clamp(64px, 8vw, 120px)',
      }}>

        {/* Pending section */}
        {pending.length > 0 && (
          <section style={{ marginBottom: 'clamp(40px, 4.5vw, 64px)' }}>
            <SectionHead label="Being prepared" count={pending.length} />
            {pending.map(function(p) {
              var statusLabel = p.status === 'pending_interpretation'
                ? 'Cleo is reading your results'
                : 'With your GP for review';
              var statusDetail = p.status === 'pending_interpretation'
                ? 'This usually takes one to two minutes.'
                : 'Your GP will release your interpretation soon, usually within 24 hours.';
              return (
                <div key={p.id} style={{
                  padding: 'clamp(18px, 2vw, 26px)',
                  background: P.PAP,
                  border: '1px dashed ' + P.SAND3,
                  marginBottom: 10,
                }}>
                  <div style={{
                    fontFamily: FF,
                    fontSize: 'clamp(16px, 1.3vw, 18px)',
                    fontWeight: 500,
                    color: P.INK,
                    marginBottom: 6,
                  }}>
                    {p.collection_date
                      ? 'Collected ' + formatDate(p.collection_date)
                      : 'Uploaded ' + formatDateShort(p.uploaded_at)}
                  </div>
                  <div style={{
                    ...T.label,
                    fontSize: 'clamp(11px, 0.9vw, 13px)',
                    color: P.BR2,
                    marginBottom: 6,
                  }}>{statusLabel}</div>
                  <div style={{
                    ...T.bodySm,
                    color: P.TF,
                    lineHeight: 1.75,
                  }}>{statusDetail}</div>
                </div>
              );
            })}
          </section>
        )}

        {/* Empty state */}
        {results.length === 0 && pending.length === 0 && (
          <div style={{
            padding: 'clamp(48px, 6vw, 80px) clamp(24px, 3vw, 40px)',
            textAlign: 'center',
            background: 'rgba(250, 246, 238, 0.55)',
            border: '1px dashed ' + P.SAND3,
          }}>
            <p style={{
              ...T.premise,
              fontSize: 'clamp(20px, 2vw, 28px)',
              color: P.BR2,
              marginBottom: 16,
            }}>You have no results yet.</p>
            <p style={{
              ...T.bodyMd,
              color: P.TL,
              maxWidth: 460,
              margin: '0 auto clamp(28px, 3vw, 40px)',
              lineHeight: 1.75,
            }}>
              When you upload pathology results and your GP approves the interpretation, they will appear here.
            </p>
            <BtnPrimary
              onClick={function() { if (onNavigate) onNavigate('pathology-upload'); }}
              large>
              Upload your first results
            </BtnPrimary>
          </div>
        )}

        {/* Approved results */}
        {results.length > 0 && (
          <section style={{ marginBottom: 'clamp(40px, 4.5vw, 64px)' }}>
            <SectionHead label="Your results" count={results.length} />
            {results.map(function(r) {
              return (
                <ResultCard
                  key={r.id}
                  result={r}
                  expanded={expanded === r.id}
                  onToggle={function() { setExpanded(expanded === r.id ? null : r.id); }}
                  onNavigate={onNavigate}
                />
              );
            })}
          </section>
        )}

        {/* Upload more action */}
        {results.length > 0 && (
          <div style={{ textAlign: 'center', marginTop: 'clamp(28px, 3vw, 40px)' }}>
            <BtnGhost onClick={function() { if (onNavigate) onNavigate('pathology-upload'); }}>
              Upload new results
            </BtnGhost>
          </div>
        )}
      </main>

      {/* Print styles + responsive action row */}
      <style>{`
        @media print {
          body { background: white !important; }
          button { display: none !important; }
        }
        @media (max-width: 640px) {
          .cs-result-actions > div { min-width: 100% !important; }
        }
      `}</style>
    </div>
  );
}
